import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Session } from "@supabase/supabase-js";
import { Search } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { supabaseV2 } from "../lib/supabase";
import { startActivityHeartbeat, touchActivityForTeam } from "../lib/activity";
import { canManageTeam, isFounderRole } from "../lib/labels";
import { PermissionsContext, type Permissions } from "../lib/permissions";
import { DesktopSidebar } from "../components/shell/DesktopSidebar";
import { RosterNavItem } from "../components/shell/RosterNavItem";
import { SettingsNavItem } from "../components/shell/SettingsNavItem";
import { SupportTriggerButton } from "../features/support/SupportTriggerButton";
import { CommandPalette } from "../components/shell/CommandPalette";
import { AppFooter } from "../components/shell/AppFooter";
import { AppAlertBars } from "../components/shell/AppAlertBars";
import { SeasonSwitcher } from "../components/shell/SeasonSwitcher";
import { SeasonClosedBanner } from "../components/shell/SeasonClosedBanner";
import { SquadScoreNotice } from "../components/shell/SquadScoreNotice";
import { SeasonProvider } from "../lib/seasons";
import { configureSwNavigationBridge, refreshSubscriptionIfNeeded } from "../lib/push";
import { viewTitle, type View } from "../components/shell/nav-config";

import { TabBar } from "../components/shell/TabBar";
import { RosterTabs } from "../components/shell/RosterTabs";
import { ClubView } from "../features/club/ClubView";
import { track } from "../lib/analytics";
import { TeamCrest } from "../components/primitives";
import { FullScreenState } from "../components/state";
import { Onboarding } from "../features/auth/Onboarding";
import { PlayerArea } from "../features/player-area/PlayerArea";
import { HomeView } from "../features/home/HomeView";
import { PlayersView } from "../features/players/PlayersView";
import { PlayersComparisonView } from "../features/players/PlayersComparisonView";
import { Top11View } from "../features/players/Top11View";
import { ArchetypesView } from "../features/archetypes/ArchetypesView";
import { GuestsView } from "../features/guests/GuestsView";
import { SessionsView } from "../features/sessions/SessionsView";
import { StatsView } from "../features/stats/StatsView";
import { SeasonsView } from "../features/seasons/SeasonsView";
import { RequestsView } from "../features/requests/RequestsView";
import { EaWorldView } from "../features/ea-world/EaWorldView";
import { MatchDetailDialog } from "../features/ea-world/MatchDetailDialog";
import type { EaMatch } from "../features/ea-world/types";
import { SettingsView } from "../features/settings/SettingsView";
import { TeamIdentityCard } from "../features/settings/TeamIdentityCard";
import { TeamMembersManager } from "../features/admin/TeamMembersManager";
import { AdminView } from "../features/admin/AdminView";
import { PlayerDetailView } from "../features/player-detail/PlayerDetailView";
import type { AppRole, Membership, Team, TeamMemberRole } from "../types";

const VIEW_PATHS: Record<Exclude<View, "player">, string> = {
  home: "/home",
  players: "/players",
  "compare-players": "/players/compare",
  top11: "/players/top11",
  archetypes: "/players/archetypes",
  guests: "/guests",
  sessions: "/sessions",
  stats: "/stats",
  seasons: "/seasons",
  ea: "/ea",
  requests: "/requests",
  club: "/club",
  settings: "/settings",
  admin: "/admin",
};

// Alias di URL "umane" che redirigono al path canonico (vedi useEffect).
const PATH_ALIASES: Record<string, string> = {
  "/statistics": "/stats",
  "/mondo-fc": "/ea",
};

function deriveViewFromPath(pathname: string): { view: View; selectedPlayerId: string | null } {
  if (pathname === "/" || pathname === "/home" || pathname === "") {
    return { view: "home", selectedPlayerId: null };
  }
  // /players/compare e' una vista a sé (Confronto giocatori), non un player detail.
  // Va matchato PRIMA del prefisso /players/<uuid>.
  if (pathname === "/players/compare" || pathname.startsWith("/players/compare/")) {
    return { view: "compare-players", selectedPlayerId: null };
  }
  if (pathname === "/players/top11" || pathname.startsWith("/players/top11/")) {
    return { view: "top11", selectedPlayerId: null };
  }
  if (pathname === "/players/archetypes" || pathname.startsWith("/players/archetypes/")) {
    return { view: "archetypes", selectedPlayerId: null };
  }
  if (pathname.startsWith("/players/")) {
    const id = pathname.slice("/players/".length).split("/")[0] || null;
    return { view: "player", selectedPlayerId: id };
  }
  const aliasTarget = PATH_ALIASES[pathname];
  if (aliasTarget) {
    for (const [view, path] of Object.entries(VIEW_PATHS)) {
      if (path === aliasTarget) return { view: view as View, selectedPlayerId: null };
    }
  }
  for (const [view, path] of Object.entries(VIEW_PATHS)) {
    if (pathname === path || pathname.startsWith(`${path}/`)) {
      return { view: view as View, selectedPlayerId: null };
    }
  }
  return { view: "home", selectedPlayerId: null };
}

export function AuthenticatedApp({ session }: { session: Session }) {
  const { t, i18n } = useTranslation("shell");
  const location = useLocation();
  const navigate = useNavigate();
  const { view: activeView, selectedPlayerId } = deriveViewFromPath(location.pathname);
  const setActiveView = (v: View) => {
    if (v === "player") return; // handled by openPlayer
    navigate(VIEW_PATHS[v as Exclude<View, "player">]);
  };
  // Squadra attiva: ricordata per utente in localStorage, cosi' un
  // ricaricamento (aggiornamento dell'app, refresh) non riporta alla prima
  // squadra. Viene convalidata piu' sotto contro membership e, per l'admin,
  // contro la lista di tutti i team.
  const teamStorageKey = `eb.activeTeam.${session.user.id}`;
  const [activeTeamId, setActiveTeamIdState] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(teamStorageKey) || null;
    } catch {
      return null;
    }
  });
  const setActiveTeamId = (id: string | null) => {
    setActiveTeamIdState(id);
    try {
      if (id) window.localStorage.setItem(teamStorageKey, id);
      else window.localStorage.removeItem(teamStorageKey);
    } catch {
      /* ignore */
    }
  };
  // Presenza: aggiorna user_activity all'apertura e ai ritorni in primo piano
  // (vedi lib/activity.ts). Il team corrente viene letto al momento del tocco.
  const activeTeamRef = useRef<string | null>(null);
  activeTeamRef.current = activeTeamId;
  useEffect(() => startActivityHeartbeat(() => activeTeamRef.current), [session.user.id]);
  useEffect(() => { touchActivityForTeam(activeTeamId); }, [activeTeamId]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteEaMatch, setPaletteEaMatch] = useState<EaMatch | null>(null);
  const [forceOnboarding, setForceOnboarding] = useState(false);
  useEffect(() => {
    configureSwNavigationBridge((url) => navigate(url));
    refreshSubscriptionIfNeeded().catch(() => {});
  }, [navigate]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const queryClient = useQueryClient();

  const membershipsQuery = useQuery({
    queryKey: ["memberships", session.user.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("team_memberships")
        .select(
          "id, team_id, user_id, role, status, teams(id, name, slug, abbreviation, primary_color, secondary_color, logo_url, ea_club_id, ea_platform, ea_game)"
        )
        .eq("user_id", session.user.id)
        .eq("status", "active");
      if (error) throw error;
      return (data || []) as unknown as Membership[];
    },
  });

  // pendingRequestsCount calcolato dopo effectiveRole/isAppAdmin: le 2 RPC
  // sono accessibili solo a founder/coach (vedi can_manage_team gate). Per
  // i player normali la query falliva con 400 (sporcando la console e
  // contaminando i retry di react-query) anche se il badge non veniva poi
  // mostrato. Vedi anche il setup di pendingRequestsCount qui sotto.

  // Realtime: invalida query su mutation lato server.
  useEffect(() => {
    // Forza il JWT corrente sul client realtime: alcune versioni della libreria
    // non lo rinfrescano in automatico quando il channel viene creato dopo il login.
    if (session.access_token) {
      supabaseV2.realtime.setAuth(session.access_token);
    }
    const channel = supabaseV2
      .channel(`eb-realtime-${session.user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "team_join_requests" }, () => {
        queryClient.invalidateQueries({ queryKey: ["pending-join-requests-count"] });
        queryClient.invalidateQueries({ queryKey: ["join-requests"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "team_memberships" }, () => {
        queryClient.invalidateQueries({ queryKey: ["unlinked-members-count"] });
        queryClient.invalidateQueries({ queryKey: ["unlinked-members"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "players" }, () => {
        queryClient.invalidateQueries({ queryKey: ["players-missing-ea-count"] });
        queryClient.invalidateQueries({ queryKey: ["unlinked-members-count"] });
        queryClient.invalidateQueries({ queryKey: ["unlinked-members"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "session_responses" }, () => {
        queryClient.invalidateQueries({ queryKey: ["responses"] });
        queryClient.invalidateQueries({ queryKey: ["workspace"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "session_callups" }, () => {
        queryClient.invalidateQueries({ queryKey: ["session-callups"] });
        queryClient.invalidateQueries({ queryKey: ["session-callups-match"] });
        queryClient.invalidateQueries({ queryKey: ["player-callups"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, () => {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      })
      .subscribe((status, err) => {
        if (status === "SUBSCRIBED") {
          console.info("[realtime] subscribed", channel.topic);
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
          console.warn("[realtime] subscription issue", status, err);
        }
      });
    return () => { supabaseV2.removeChannel(channel); };
  }, [session.user.id, session.access_token, queryClient]);

  const appRolesQuery = useQuery({
    queryKey: ["app-roles", session.user.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("user_app_roles")
        .select("role")
        .eq("user_id", session.user.id);
      if (error) {
        console.error("[app-roles] query error", error);
        throw error;
      }
      return (data || []).map((r: any) => r.role as AppRole);
    },
    staleTime: 60_000,
  });
  const appRoles = appRolesQuery.data || [];
  const isAppAdmin = appRoles.includes("super_admin") || appRoles.includes("admin_support");
  const isSuperAdmin = appRoles.includes("super_admin");

  // useMemo per stabilizzare il reference: `query.data || []` creerebbe
  // un nuovo array a ogni render e farebbe scattare di continuo gli
  // useEffect che lo hanno in deps.
  const memberships = useMemo(() => membershipsQuery.data || [], [membershipsQuery.data]);
  const membershipsLoaded = membershipsQuery.isSuccess;

  const allTeamsQuery = useQuery({
    queryKey: ["admin-teams-list"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("teams")
        .select("id, name, slug, abbreviation, primary_color, secondary_color, logo_url, ea_club_id, ea_platform, ea_game")
        .order("name");
      if (error) throw error;
      return (data || []) as Team[];
    },
    enabled: isAppAdmin,
    staleTime: 60_000,
  });
  const allTeams = useMemo(() => allTeamsQuery.data || [], [allTeamsQuery.data]);
  const allTeamsLoaded = allTeamsQuery.isSuccess;

  const membershipForActive = activeTeamId
    ? memberships.find((m) => m.team_id === activeTeamId)
    : undefined;
  const adminTeamForActive = isAppAdmin && activeTeamId
    ? allTeams.find((t) => t.id === activeTeamId)
    : undefined;
  const team: Team | undefined = membershipForActive?.teams || adminTeamForActive;
  const activeMembership = membershipForActive;
  const effectiveRole: TeamMemberRole = activeMembership?.role
    || (isAppAdmin && team ? "founder" : "player");
  // Gate per le 2 query "richieste in coda": solo founder/coach o app admin
  // possono leggere queste RPC (lato server hanno can_manage_team()), quindi
  // niente fetch quando l'utente è player normale.
  const canSeeJoinRequests = canManageTeam(effectiveRole) || isAppAdmin;

  const pendingRequestsQuery = useQuery({
    queryKey: ["pending-join-requests-count", session.user.id, activeTeamId],
    enabled: !!activeTeamId && canSeeJoinRequests,
    queryFn: async () => {
      const { count, error } = await supabaseV2
        .from("team_join_requests")
        .select("id", { count: "exact", head: true })
        .eq("team_id", activeTeamId!)
        .eq("status", "pending");
      if (error) throw error;
      return count || 0;
    },
  });
  // Membri entrati via link diretto ma ancora senza player rosa collegato:
  // anche loro vengono mostrati nella vista Richieste, quindi entrano nel badge.
  const unlinkedMembersQuery = useQuery({
    queryKey: ["unlinked-members-count", session.user.id, activeTeamId],
    enabled: !!activeTeamId && canSeeJoinRequests,
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_unlinked_team_members", {
        _team_id: activeTeamId!,
      });
      if (error) throw error;
      return (data || []).length;
    },
  });
  const pendingRequestsCount = (pendingRequestsQuery.data || 0) + (unlinkedMembersQuery.data || 0);
  // Giocatori attivi senza nome EA: numero accanto a "Rosa". Solo per lo
  // staff e solo se la squadra ha un club EA (i dati EA restano facoltativi).
  const missingEaQuery = useQuery({
    queryKey: ["players-missing-ea-count", activeTeamId],
    enabled: !!activeTeamId && canSeeJoinRequests && !!team?.ea_club_id,
    queryFn: async () => {
      const { count, error } = await supabaseV2
        .from("players")
        .select("id", { count: "exact", head: true })
        .eq("team_id", activeTeamId!)
        .eq("status", "active")
        .eq("is_guest", false)
        .is("ea_account_handle", null)
        .is("ea_sports_id", null);
      if (error) throw error;
      return count || 0;
    },
  });
  const missingEaCount = missingEaQuery.data || 0;

  useEffect(() => {
    if (activeTeamId) {
      // Team ricordato ma non piu' raggiungibile (uscito dal team, ruolo admin
      // tolto): si riparte dalla prima squadra. Si decide solo a dati caricati,
      // ruoli compresi: finche' non arrivano un admin sembra un utente normale
      // e perderebbe la squadra che stava guardando.
      if (!membershipsLoaded || appRolesQuery.isLoading) return;
      const isMember = memberships.some((m) => m.team_id === activeTeamId);
      if (isMember) return;
      if (isAppAdmin && (!allTeamsLoaded || allTeams.some((t) => t.id === activeTeamId))) return;
      setActiveTeamId(null);
      return;
    }
    if (memberships[0]?.team_id) {
      setActiveTeamId(memberships[0].team_id);
    } else if (isAppAdmin && allTeams[0]?.id) {
      setActiveTeamId(allTeams[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeamId, memberships, membershipsLoaded, isAppAdmin, allTeams, allTeamsLoaded, appRolesQuery.isLoading]);

  // Al cambio squadra la scheda aperta non vale piu'. Il primo valore non e' un
  // cambio: altrimenti aprire /players/:id riporterebbe alla rosa.
  const previousTeamRef = useRef(activeTeamId);
  useEffect(() => {
    const previous = previousTeamRef.current;
    previousTeamRef.current = activeTeamId;
    if (previous && previous !== activeTeamId && activeView === "player") navigate("/players");
    // Intenzionale: deve scattare SOLO al cambio team. Includere `activeView`
    // o `navigate` causerebbe un loop quando si entra in /players/:id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeamId]);

  // Aggiorna il <title> del tab del browser in base a view + team + lingua.
  // Senza questo, il tab resta "ElevenBase · Gestione squadre eSport & Pro Clubs"
  // (statico in index-v2.html) anche per utenti EN/DE.
  useEffect(() => {
    const head = team?.name
      ? viewTitle(activeView, team.name)
      : viewTitle(activeView);
    document.title = `${head} · ElevenBase`;
  }, [activeView, team?.name, i18n.language]);

  // page_view su ogni cambio di view (SPA): GTM puo poi forwardare a GA4.
  useEffect(() => {
    // Usa `team?.id`/`team?.name` invece di `team` per evitare di mettere
    // l'oggetto `team` intero in deps (cambierebbe reference a ogni refetch).
    if (!team?.id) return;
    track.pageView({
      page_path: location.pathname,
      page_title: viewTitle(activeView, team.name),
      view: activeView,
      team_id: team.id,
    });
  }, [activeView, selectedPlayerId, location.pathname, team?.id, team?.name]);

  // Staff: normalizza path / o /me a /home (per chi non è player).
  useEffect(() => {
    const isPlayerOnly = effectiveRole === "player" && !isAppAdmin;
    if (isPlayerOnly) return;
    if (location.pathname === "/" || location.pathname.startsWith("/me")) {
      navigate("/home", { replace: true });
    }
  }, [location.pathname, navigate, effectiveRole, isAppAdmin]);

  // Alias URL "umane" → URL canoniche (es. /statistics → /stats, /mondo-fc → /ea).
  useEffect(() => {
    const target = PATH_ALIASES[location.pathname];
    if (target) navigate(target, { replace: true });
  }, [location.pathname, navigate]);

  const openPlayer = (playerId: string) => {
    track.selectContent("player", playerId, { team_id: activeTeamId });
    navigate(`/players/${playerId}`);
  };

  const goToView = (view: View, source: "sidebar" | "mobile_sheet" | "palette" | "shortcut" | "header" = "sidebar") => {
    track.navigation(view, source);
    setActiveView(view);
  };

  const signOut = async () => {
    track.logout();
    await supabaseV2.auth.signOut();
    queryClient.clear();
  };

  // Squadra non ancora risolvibile: per un admin che guarda una squadra di
  // cui non e' membro, al refresh le membership arrivano prima dei ruoli e
  // dell'elenco di tutte le squadre, e senza questa attesa si vedeva per un
  // attimo l'onboarding. Stesso discorso quando l'effetto sopra sta per
  // scegliere la prima squadra disponibile.
  const teamPending = !team && !forceOnboarding && (
    appRolesQuery.isLoading
    || (isAppAdmin && !allTeamsLoaded && !allTeamsQuery.isError)
    || (!activeTeamId && (memberships.length > 0 || (isAppAdmin && allTeams.length > 0)))
    // Squadra salvata di cui non si e' (piu') membri: l'effetto sopra sta per
    // tornare alla prima squadra disponibile.
    || (!!activeTeamId && !isAppAdmin && memberships.length > 0)
  );

  if (membershipsQuery.isLoading || teamPending) {
    return <FullScreenState title={t("common.loadingTeamTitle", { ns: "common" })} description={t("common.loadingTeamDesc", { ns: "common" })} />;
  }

  if (forceOnboarding || !team || (!activeMembership && !isAppAdmin)) {
    return (
      <Onboarding
        userEmail={session.user.email || ""}
        onDone={() => {
          setForceOnboarding(false);
          queryClient.invalidateQueries({ queryKey: ["memberships"] });
        }}
        onSignOut={signOut}
      />
    );
  }

  if (effectiveRole === "player" && !isAppAdmin) {
    return (
      <SeasonProvider key={team.id} teamId={team.id}>
        <PlayerArea
          session={session}
          team={team}
          role={effectiveRole}
          onSignOut={signOut}
          onCreateNewTeam={() => setForceOnboarding(true)}
        />
      </SeasonProvider>
    );
  }

  const mainView = (() => {
    switch (activeView) {
      case "home":
        return <HomeView team={team} onOpenPlayer={openPlayer} onChangeView={goToView} />;
      case "players":
        return <PlayersView teamId={team.id} hasEaClub={!!team.ea_club_id} onOpenPlayer={openPlayer} />;
      case "compare-players":
        return <PlayersComparisonView teamId={team.id} onOpenPlayer={openPlayer} />;
      case "top11":
        return <Top11View teamId={team.id} onOpenPlayer={openPlayer} />;
      case "archetypes":
        return <ArchetypesView team={team} onOpenPlayer={openPlayer} />;
      case "guests":
        return <GuestsView teamId={team.id} teamName={team.name} onOpenPlayer={openPlayer} />;
      case "sessions":
        return <SessionsView teamId={team.id} />;
      case "stats":
        return <StatsView teamId={team.id} onOpenPlayer={openPlayer} />;
      case "seasons":
        return <SeasonsView teamId={team.id} onOpenPlayer={openPlayer} />;
      case "requests":
        return canSeeJoinRequests ? <RequestsView teamId={team.id} /> : <Navigate to="/home" replace />;
      case "ea":
        return (
          <EaWorldView
            teamId={team.id}
            eaClubId={team.ea_club_id || null}
            eaPlatform={team.ea_platform || "common-gen5"}
            eaGame={team.ea_game || null}
            onOpenSettings={() => goToView("settings")}
          />
        );
      case "settings":
        return (
          <SettingsView
            team={team}
            role={effectiveRole}
            isAppAdmin={isAppAdmin}
            userId={session.user.id}
            userEmail={session.user.email || ""}
            onCreateNewTeam={() => setForceOnboarding(true)}
            TeamIdentityCard={TeamIdentityCard}
            TeamMembersManager={TeamMembersManager}
          />
        );
      case "club":
        return (
          <ClubView
            team={team}
            teams={isAppAdmin ? allTeams : (memberships.map((m) => m.teams).filter(Boolean) as Team[])}
            role={effectiveRole}
            userEmail={session.user.email || ""}
            canSeeRequests={canSeeJoinRequests}
            canManageMembers={isFounderRole(effectiveRole) || isAppAdmin}
            isAppAdmin={isAppAdmin}
            requestsBadge={pendingRequestsCount}
            onSelect={goToView}
            onSwitchTeam={(id) => { track.teamSwitch(id); setActiveTeamId(id); }}
            onSignOut={signOut}
          />
        );
      case "player":
        return (
          <PlayerDetailView
            teamId={team.id}
            playerId={selectedPlayerId}
            onBack={() => navigate("/players")}
          />
        );
      case "admin":
        return isAppAdmin ? (
          <AdminView
            isSuperAdmin={isSuperAdmin}
            currentTeamId={team?.id || null}
            onOpenTeam={(id) => {
              track.teamSwitch(id);
              setActiveTeamId(id);
              navigate("/home");
            }}
          />
        ) : null;
      default:
        return null;
    }
  })();

  const permissions: Permissions = {
    role: effectiveRole,
    canManage: canManageTeam(effectiveRole) || isAppAdmin,
    canManageMembers: isFounderRole(effectiveRole) || isAppAdmin,
    isAppAdmin,
    isSuperAdmin,
  };

  // Scorciatoie del bottom nav mobile. Qui siamo in modalità staff (player
  // ha PlayerArea separato), quindi default uguale per founder/coach con
  // "Richieste" mostrato solo se il ruolo può gestire i membri.
  return (
    <PermissionsContext.Provider value={permissions}>
      <SeasonProvider key={team.id} teamId={team.id}>
        <div className="min-h-screen flex">
          <DesktopSidebar
            team={team}
            activeView={activeView}
            onSelect={goToView}
            userEmail={session.user.email || ""}
            role={effectiveRole}
            isAppAdmin={isAppAdmin}
            onSignOut={signOut}
            teams={isAppAdmin ? allTeams : memberships.map((m) => m.teams).filter(Boolean) as Team[]}
            onSwitchTeam={(id) => setActiveTeamId(id)}
            pendingRequestsCount={pendingRequestsCount}
            rosterBadge={missingEaCount}
          />
          <main className="flex-1 min-w-0 flex flex-col">
            <header className="sticky top-0 z-20 bg-background border-b lg:border-b-0">
              <div className="px-4 sm:px-6 py-3 flex items-center gap-3">
                <div className="lg:hidden"><TeamCrest team={team} size={32} /></div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-[22px] lg:text-[26px] font-bold leading-tight tracking-tight truncate">{viewTitle(activeView)}</h1>
                </div>
                <Button variant="outline" className="h-10 w-10 p-0 lg:w-64 lg:justify-start lg:px-4 text-muted-foreground font-normal" onClick={() => setPaletteOpen(true)} aria-label={t("mobileNav.search")}>
                  <Search className="h-4 w-4" />
                  <span className="hidden lg:inline">{t("mobileNav.search")}</span>
                </Button>
                <SupportTriggerButton activeTeamId={team?.id || null} />
                {memberships.length > 1 && (
                  <Select value={team.id} onValueChange={(value) => { track.teamSwitch(value); setActiveTeamId(value); }}>
                    <SelectTrigger className="w-44 hidden sm:flex">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {memberships.map((membership) => (
                        <SelectItem key={membership.team_id} value={membership.team_id}>
                          {membership.teams?.name || membership.team_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </header>
            <SeasonClosedBanner />
            <AppAlertBars teamId={team.id} activeView={activeView} onNavigate={goToView} />
            <SquadScoreNotice userId={session.user.id} className="px-4 sm:px-6 pt-3 max-w-[1600px] w-full mx-auto" />
            <div className="flex-1 px-4 sm:px-6 py-5 lg:py-6 pb-28 lg:pb-6 max-w-[1600px] w-full mx-auto">
              <RosterTabs activeView={activeView} onSelect={(v) => goToView(v, "mobile_sheet")} />
              {mainView}
            </div>
            <AppFooter />
          </main>
          <CommandPalette
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            teamId={team.id}
            currentView={activeView}
            onChangeView={(v) => goToView(v, "palette")}
            onOpenPlayer={openPlayer}
            teams={isAppAdmin ? allTeams : (memberships.map((m) => m.teams).filter(Boolean) as Team[])}
            activeTeamId={team.id}
            onSwitchTeam={(id) => { track.teamSwitch(id); setActiveTeamId(id); }}
            showRequests={permissions.canManageMembers}
            showAdmin={isAppAdmin}
            canManageMembers={permissions.canManageMembers}
            eaClubId={team.ea_club_id || null}
            onOpenEaMatch={(m) => { track.selectContent("ea_match", m.ea_match_id, { source: "palette" }); setPaletteEaMatch(m); }}
          />
          {team.ea_club_id && (
            <MatchDetailDialog
              match={paletteEaMatch}
              eaClubId={team.ea_club_id}
              open={!!paletteEaMatch}
              onOpenChange={(next) => { if (!next) setPaletteEaMatch(null); }}
            />
          )}
          <TabBar activeView={activeView} onSelect={(v) => goToView(v, "mobile_sheet")} badge={pendingRequestsCount} />
        </div>
      </SeasonProvider>
    </PermissionsContext.Provider>
  );
}
